import React, { useState, useEffect, useRef } from "react";
import PageNavigationItem from "./PageNavigationItem";
import { __ } from "localisation";
import "./PageNavigation.scss";
import { useHistory } from "react-router-dom";

/**
 * PageNavigation is a component that displays a list of navigation items for a page.
 * The navigation items are defined by adding a data-page-navigation-anchor attribute to the elements that we want to include in the navigation.
 * Optionally, you can add a data-page-navigation-anchor-label attribute to the element to override the label for the navigation item.
 * <div id="due_diligence_checks" data-page-navigation-anchor={__('due_diligence_checks')}>
 *   ...
 * </div>
 */

interface PageNavigationProps {
  mounted: boolean;
  preventHistory?: boolean;
}

const isElementVisible = (element: HTMLElement): boolean => {
  let currentElement: HTMLElement | null = element;
  while (currentElement) {
    if (currentElement.hidden) {
      return false;
    }
    const style = window.getComputedStyle(currentElement);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }
    currentElement = currentElement.parentElement;
  }
  return true;
};

const PageNavigation = ({ mounted, preventHistory = false }: PageNavigationProps) => {
  const history = useHistory();

  const [items, setItems] = useState<{ id: string; label: string }[]>([]);
  const [hash, setHash] = useState<string | null>(null);
  const elements = useRef<NodeListOf<HTMLElement> | null>(null);

  const init = () => {
    // Initialize an intersection observer and check which navigation element is active

    // Query all elements with data-page-navigation-anchor attribute and filter out hidden ones
    const allElements = document.querySelectorAll('[data-page-navigation-anchor]') as NodeListOf<HTMLElement>;
    elements.current = Array.from(allElements).filter(isElementVisible) as unknown as NodeListOf<HTMLElement>;

    const intersectionObserverOptions = {
      rootMargin: "0px 0px -99% 0px",
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      setHash(null);

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setHash(entry.target.id);
        }
      });
    }, intersectionObserverOptions);

    elements.current.forEach((element) => {
      observer.observe(element);
    });

    // Create navigation items
    const items = Array.from(elements.current).map((element) => ({
      id: element.id,
      label: element.dataset.pageNavigationAnchor === "true" ? element.id : element.dataset.pageNavigationAnchor || element.id
    }));

    setItems(items);
  }

  useEffect(() => {
    history.replace(`#${hash || ""}`);
  }, [hash]);


  useEffect(() => {
    if (mounted) init();
  }, [mounted]);

  const onClickItem = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    if (preventHistory) {
      e.preventDefault();

      // Scroll to the first visible element with the given id
      const allElements = document.querySelectorAll(`#${id}`) as NodeListOf<HTMLElement>;
      const elements = Array.from(allElements).filter(isElementVisible) as unknown as NodeListOf<HTMLElement>;

      elements[0].scrollIntoView({ behavior: 'smooth' });
    }
  }

  if (!mounted) return null;

  return (
    <div className="page-navigation">
      <div className="page-navigation__items">
        {items && items.map((item) => (
          <PageNavigationItem key={item.id} label={item.label} href={`#${item.id}`} id={item.id} active={item.id === hash} onClick={onClickItem} />
        ))}
      </div>
    </div>
  );
};

export default PageNavigation;